import { Button, Dropdown, Space } from "antd";
import { Children, cloneElement, isValidElement, type ReactElement, type ReactNode } from "react";
import { Tag, Table as ZhTable } from "zhihao-ui-react";
import { getWidthByText } from "@/utils/getWidthByText";
import type { ColumnProps, RenderScope } from "@/views/components/ProTable/interface";
import { filterEnum, filterEnumObj, formatValue, handleRowAccordingToProp } from "@/utils";

export interface TableColumnProps<T extends Record<string, any> = Record<string, any>> {
    column: ColumnProps<T>;
    enumMap?: Map<string, any[]>;
    operation?: ReactNode;
}

type OperationTextMeasure = (text: string) => number;

/** 递归读取操作按钮的可见文字，等价于 Vue TableColumn 对 VNode children 的读取。 */
function getOperationNodeText(node: ReactNode): string {
    if (typeof node === "string" || typeof node === "number") return String(node);
    if (Array.isArray(node)) return node.map(getOperationNodeText).join("");
    if (!isValidElement(node)) return "";
    return getOperationNodeText((node.props as { children?: ReactNode }).children);
}

/** 识别 Vue 操作 slot 中的按钮节点，其他布局节点继续递归查找。 */
function isOperationAction(node: ReactElement): boolean {
    const props = node.props as { onClick?: unknown };
    if (props.onClick != null) return true;
    if (node.type === "button" || node.type === "a") return true;
    const type = node.type as { displayName?: string; name?: string; render?: { name?: string } };
    const typeName = type.displayName || type.name || type.render?.name || "";
    return typeName === "Button" || typeName.endsWith("Button");
}

/** 收集操作列原始按钮，保持 Vue 旧版先统计原始动作、再折叠“更多”的顺序。 */
export function getOperationActionNodes(content: ReactNode): ReactElement[] {
    if (Array.isArray(content)) return content.flatMap(getOperationActionNodes);
    if (!isValidElement(content)) return [];
    if (isOperationAction(content)) return [content];
    return getOperationActionNodes((content.props as { children?: ReactNode }).children);
}

/** 复刻旧版 getButtonsWidth + setButtonsWidth 的精确宽度公式。 */
export function calculateVueOperationWidth(
    actionTexts: string[],
    measureText: OperationTextMeasure = getWidthByText
): number {
    const visibleTexts = actionTexts.length <= 3 ? actionTexts : [...actionTexts.slice(0, 2), "更多"];
    if (!visibleTexts.length) return 0;

    const totalTextWidth = measureText(visibleTexts.join(""));
    const buttonWidth = totalTextWidth + 6 * visibleTexts.length + (8 * visibleTexts.length - 1);
    return Math.round(buttonWidth) + 32;
}

interface OperationColumnWidthResult {
    width?: number;
    dynamic: boolean;
    renderedRows: WeakMap<object, ReactNode>;
}

/** 根据已加载数据模拟 Vue TableColumn 的逐行最大宽度收敛行为。 */
function getVueOperationColumnWidth<T extends Record<string, any>>(
    column: T,
    data?: unknown[]
): OperationColumnWidthResult {
    const renderedRows = new WeakMap<object, ReactNode>();
    if (!column.render || !data?.length) return { dynamic: false, renderedRows };

    let maxWidth = 0;
    let dynamic = false;
    const render = column.render as (scope: RenderScope<any>) => ReactNode;
    data.forEach((row, rowIndex) => {
        if (!row || typeof row !== "object") return;
        const objectRow = row as Record<string, any>;
        const rendered = render({ row: objectRow, $index: rowIndex, column } as RenderScope<any>);
        renderedRows.set(objectRow, rendered);
        const actions = getOperationActionNodes(rendered);
        if (!actions.length) return;

        dynamic = true;
        maxWidth = Math.max(
            maxWidth,
            calculateVueOperationWidth(
                actions.map(action => getOperationNodeText((action.props as { children?: ReactNode }).children))
            )
        );
    });

    return { width: dynamic ? maxWidth : undefined, dynamic, renderedRows };
}

/** 计算无数据或无法识别动作时的迁移期兜底宽度。 */
function getStaticOperationWidth(column: Record<string, any>): number {
    const declaredWidth = Number(column.width);
    const compactOperation = String(column.className ?? "").includes("compact");
    const supplierOperation = String(column.className ?? "").includes("supplier-operation-column");
    if (supplierOperation) return 134;
    if (compactOperation) return Number.isFinite(declaredWidth) ? declaredWidth : 134;
    return Number.isFinite(declaredWidth) ? Math.max(declaredWidth, 188) : 188;
}

function getOperationColumnClassName(column: Record<string, any>, dynamic: boolean): string {
    return [column.className ?? "admin-table-operation-column", dynamic ? "vue-operation-auto-width" : undefined]
        .filter(Boolean)
        .join(" ");
}

/**
 * 按 Vue TableColumn 的约定收拢过多操作：保留前两个动作，其余动作放入“更多”。
 * 仅处理操作列的直接子节点，避免改变普通单元格和已有自定义下拉菜单。
 */
export function normalizeOperationActions(content: ReactNode): ReactNode {
    // React render 函数既可能返回包裹节点，也可能直接返回动作数组；两种形态都要遵循 Vue 的操作列折叠规则。
    const actionSource = isValidElement(content)
        ? (content.props as { children?: ReactNode }).children
        : Array.isArray(content)
          ? content
          : undefined;
    if (actionSource === undefined) return content;

    const children = Children.toArray(actionSource);
    if (children.length <= 3) return content;

    const overflowItems = children.slice(2).map((child, index) => ({
        key: `operation-${index}`,
        label: child
    }));
    const visibleActions = [
        ...children.slice(0, 2),
        <Dropdown key="operation-more" trigger={["click"]} menu={{ items: overflowItems }}>
            <Button className="operation-more-action" type="link">
                更多
            </Button>
        </Dropdown>
    ];

    if (isValidElement(content)) {
        return cloneElement(content as ReactElement<{ children?: ReactNode }>, { children: visibleActions });
    }

    // 直接数组没有可复用的宿主节点，补充与页面现有操作列一致的紧凑布局容器。
    return (
        <Space size={0} wrap>
            {visibleActions}
        </Space>
    );
}

/**
 * 为直接使用 zhihao Table 的页面复用 Vue 操作列溢出规则。
 * ProTable 会自动经过 TableColumn 适配层，但页面直传 columns 时也必须显式接入同一契约。
 */
export function normalizeOperationColumns<T extends Record<string, any>>(columns: T[], data?: unknown[]): T[] {
    return columns.map(column => {
        const prop = String(column.prop ?? column.key ?? "");
        if (!column.render || (prop !== "operation" && prop !== "operations" && column.label !== "操作")) {
            return column;
        }

        const { width: dynamicWidth, dynamic, renderedRows } = getVueOperationColumnWidth(column, data);
        const operationWidth = dynamic ? dynamicWidth : getStaticOperationWidth(column);
        const render = column.render as (scope: unknown) => ReactNode;
        return {
            ...column,
            width: operationWidth,
            className: getOperationColumnClassName(column, dynamic),
            labelClassName: getOperationColumnClassName(
                { ...column, className: column.labelClassName ?? column.className },
                dynamic
            ),
            render: (scope: any) => {
                const cached = renderedRows.get(scope.row as object);
                return normalizeOperationActions(cached ?? render(scope));
            }
        } as T;
    });
}

/** 将旧 Vue 列配置转换为 zhihao-ui-react 的列配置。 */
export function toReactTableColumn<T extends Record<string, any>>(
    column: ColumnProps<T>,
    enumMap: Map<string, any[]> = new Map(),
    data?: unknown[]
): Record<string, any> {
    const children = column._children?.map(child => toReactTableColumn(child, enumMap, data));
    const prop = String(column.prop ?? "");
    const enumData = enumMap.get(prop);
    const isOperationColumn = prop === "operation" || prop === "operations" || column.label === "操作";
    const {
        width: dynamicWidth,
        dynamic,
        renderedRows
    } = isOperationColumn
        ? getVueOperationColumnWidth(column, data)
        : { width: undefined, dynamic: false, renderedRows: new WeakMap<object, ReactNode>() };
    const operationWidth = isOperationColumn
        ? dynamic
            ? dynamicWidth
            : getStaticOperationWidth(column)
        : column.width;

    return {
        ...column,
        ...(isOperationColumn ? { width: operationWidth } : {}),
        ...(isOperationColumn
            ? {
                  className: getOperationColumnClassName(column, dynamic),
                  labelClassName: getOperationColumnClassName(
                      { ...column, className: column.labelClassName ?? column.className },
                      dynamic
                  )
              }
            : {}),
        children,
        prop: column.prop,
        isShow: typeof column.isShow === "object" ? Boolean(column.isShow.value) : (column.isShow ?? true),
        isCustomDisable: column.isCustomDisable,
        showOverflowTooltip: column.showOverflowTooltip ?? prop !== "operation",
        className: isOperationColumn
            ? getOperationColumnClassName(column, dynamic)
            : column.wrap
              ? "table-column--wrap"
              : undefined,
        render: (scope: any) => {
            const legacyScope: RenderScope<T> = {
                row: scope.row,
                $index: scope.rowIndex ?? scope.$index ?? 0,
                column: scope.column,
                ...scope
            };
            const value = handleRowAccordingToProp(scope.row, prop);
            if (column.render) {
                const cached = renderedRows.get(scope.row as object);
                const rendered = cached ?? column.render(legacyScope);
                return isOperationColumn ? normalizeOperationActions(rendered) : rendered;
            }
            if (column.tag) {
                const tagType = enumData ? filterEnum(value, enumData, column.fieldNames, "tag") : undefined;
                return <Tag type={tagType as any}>{renderCellData(column, value, enumData)}</Tag>;
            }
            if (column.click) {
                const formatter = column.formatter as unknown as
                    | ((row: T, column: unknown, value: unknown, index: number) => ReactNode)
                    | undefined;
                return (
                    <span className="link-name" onClick={() => column.click?.(scope.row)}>
                        {formatter
                            ? formatter(scope.row, scope.column, value, legacyScope.$index)
                            : renderCellData(column, value, enumData)}
                    </span>
                );
            }
            const formatter = column.formatter as unknown as
                | ((row: T, column: unknown, value: unknown, index: number) => ReactNode)
                | undefined;
            return (
                <span
                    className={enumData ? filterEnumObj(value, enumData, column.fieldNames)?.state || "text" : "text"}
                >
                    {formatter
                        ? formatter(scope.row, scope.column, value, legacyScope.$index)
                        : renderCellData(column, value, enumData)}
                </span>
            );
        },
        renderHeader: column.headerRender
            ? (scope: any) => column.headerRender?.({ ...scope, $index: scope.columnIndex ?? 0 })
            : undefined
    };
}

/** 保留一个可直接嵌入 JSX 的列声明组件，实际列由父级 Table 统一托管。 */
export function TableColumn<T extends Record<string, any>>({ column, enumMap }: TableColumnProps<T>) {
    const normalized = toReactTableColumn(column, enumMap);
    return <ZhTable.Column {...normalized} />;
}

function renderCellData<T extends Record<string, any>>(column: ColumnProps<T>, value: unknown, enumData?: any[]) {
    return enumData && (column.isFilterEnum ?? true)
        ? filterEnum(value, enumData, column.fieldNames)
        : formatValue(value);
}

export default TableColumn;
